import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../../../hooks/use-toast";
import { nfsService } from "../services/nfs-service";
import { CompletudeResult } from "../types";

export const useFechamentoStatus = () => useQuery({
  queryKey: ["fechamento", "status"],
  queryFn: async () => {
    const response = await nfsService.getFechamentoStatus();
    return response.data;
  },
});

export const useResumoNfs = (dataInicioPeriodo?: string) => useQuery({
  queryKey: ["nfs", "resumo", dataInicioPeriodo],
  queryFn: async () => {
    if (!dataInicioPeriodo) return null;
    const response = await nfsService.getResumoNfs({ data_periodo_inicio: dataInicioPeriodo });
    return response.data;
  },
  enabled: !!dataInicioPeriodo,
});

export const useEnviarEmailFechamento = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: nfsService.enviarEmailFechamento,
    onSuccess: (response) => {
      const data = response.data;
      if (data?.semana_completa === false) {
        toast({
          title: "Aviso",
          description: "A semana ainda não está completa — email não enviado.",
          variant: "default",
        });
      } else {
        toast({
          title: "Sucesso",
          description: "Email de fechamento enviado com sucesso!",
        });
      }
      queryClient.invalidateQueries({ queryKey: ["fechamento", "status"] });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Erro ao enviar email de fechamento",
        variant: "destructive",
      });
    },
  });
};

export const useCalcularSaldoRemanescente = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: nfsService.calcularSaldoRemanescente,
    onSuccess: () => {
      toast({ title: "Sucesso", description: "Cálculo de saldo remanescente concluído!" });
    },
    onError: () => {
      toast({ title: "Erro", description: "Erro ao calcular saldo remanescente", variant: "destructive" });
    },
  });
};

export const useProcessarNfsSaldo = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: nfsService.processarNfsSaldo,
    onSuccess: (response) => {
      const result = response.data;
      if (result?.success) {
        toast({
          title: result.quantidadeSucesso > 0 ? "Sucesso" : "Aviso",
          description: `NFS processadas! ${result.quantidadeSucesso || 0} sucessos, ${result.quantidadeFalha || 0} falhas`,
        });
      } else {
        toast({ title: "Erro", description: result?.message || "Erro ao processar NFS", variant: "destructive" });
      }
    },
    onError: () => {
      toast({ title: "Erro", description: "Erro ao processar NFS para saldo remanescente", variant: "destructive" });
    },
  });
};

export const useDispararDownloadNfs = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: nfsService.dispararDownloadNfs,
    onSuccess: (response) => {
      const result = response.data;
      toast({
        title: "Job disparado",
        description: result?.message || "Download de NFS da semana iniciado em segundo plano.",
      });
    },
    onError: () => {
      toast({ title: "Erro", description: "Erro ao disparar o download de NFS", variant: "destructive" });
    },
  });
};

export const useGerarZipsNfs = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: nfsService.gerarZipsNfs,
    onSuccess: (response) => {
      const result = response.data;
      const ops = result?.opsEnfileiradas ?? [];
      toast({
        title: ops.length > 0 ? "ZIPs em geração" : "Nada a gerar",
        description:
          ops.length > 0
            ? `${ops.length} franquia(s) enfileirada(s): ${ops.join(", ")}. Busque os links em instantes.`
            : result?.message || "Nenhuma franquia com notas na semana.",
      });
    },
    onError: () => {
      toast({ title: "Erro", description: "Erro ao gerar os ZIPs por franquia", variant: "destructive" });
    },
  });
};

/**
 * Completude das NFS da semana (banco vs S3).
 *
 * Fica desligada até o usuário abrir o painel — a verificação lista o bucket
 * inteiro e não vale rodar a cada troca de data. Depois de ligada, segue a
 * data selecionada, já que ela faz parte da queryKey.
 */
export const useCompletudeNfs = (segunda: string | undefined, habilitado: boolean) =>
  useQuery({
    queryKey: ["nfs", "completude", segunda],
    queryFn: async () => {
      const response = await nfsService.verificarCompletude({ segunda: segunda! });
      return response.data as CompletudeResult;
    },
    enabled: habilitado && !!segunda,
    staleTime: 60 * 1000,
  });

export const useBuscarLinksZips = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: nfsService.buscarLinksZips,
    onError: () => {
      toast({ title: "Erro", description: "Erro ao buscar os links dos ZIPs", variant: "destructive" });
    },
  });
};

export const useEnviarEmailSaldo = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: nfsService.enviarEmailSaldo,
    onSuccess: (response) => {
      const result = response.data;
      if (result?.success) {
        toast({
          title: "Sucesso",
          description: `Email enviado! ${result.totalSaldosEncontrados || 0} registros em ${result.totalOps || 0} ops`,
        });
      } else {
        toast({ title: "Erro", description: result?.message || "Erro ao enviar email de saldo remanescente", variant: "destructive" });
      }
    },
    onError: () => {
      toast({ title: "Erro", description: "Erro ao enviar email de saldo remanescente", variant: "destructive" });
    },
  });
};
